import { useEffect, useState } from 'react'

/**
 * Track a media query in JS.
 *
 * For the cases CSS cannot answer: a Radix `side` prop, or a layout that is a
 * different COMPONENT on a phone rather than the same one restyled. Reach for
 * Tailwind's responsive classes first — this exists for when the difference is
 * structural, not cosmetic.
 *
 * Reads synchronously on first render so there is no flash of the wrong
 * layout, and guards `window` so it is safe under SSR or a test renderer.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  )

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia(query)
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches)
    mq.addEventListener('change', onChange)
    // Re-read on mount: the query may have changed between the first render
    // and the effect (a rotation, or a resize during hydration).
    setMatches(mq.matches)
    return () => mq.removeEventListener('change', onChange)
  }, [query])

  return matches
}

/** Tailwind's `md` breakpoint — the one this product splits phone from tablet on. */
export const useIsPhone = () => !useMediaQuery('(min-width: 768px)')
